// Angular imports
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

// 3rd party imports
import {
  DxProgressBarComponent,
  DxValidationGroupComponent,
  DxValidatorComponent
} from 'devextreme-angular';

// Component imports
import {
  IconConstants,
  CCPasswordHelperService
} from '@therealmanage/ciranet-ui-common';
import { RecaptchaBaseComponent } from '../../_base/recaptcha-base.component';

// Service imports
import { UserService } from '../../_services/data-services/user.service';
import { ScreenService } from '../../_services/shared-services/screen.service';
import { ServerErrorService } from '../../_services/shared-services/server-error.service';
import { EnvironmentConfigService } from '../../_services/environment-config.service';

// Model imports
import { CreateUserParameterDto } from '../../_models/create-user-parameter-dto';
import { RegistrationEditorParametersDto } from '../../_models/registration/registration-editor-parameters-dto';
import { RegistrationEditorResultDto } from '../../_models/registration/registration-editor-result-dto';
import { ValidateRegistrationCredentialsDto } from '../../_models/validate-registration-credentials-dto';
import { ValidateRegistrationCredentialsResultDto } from '../../_models/validate-registration-credentials-result-dto';

// Constant imports
import { ValidationFormatConstants } from '../../_constants/validation-format-constants';
import { ImageConstants } from '../../_constants/image-constants';
import { Constants } from '../../_constants/constants';

// CC-Common imports
import { Utils, RxjsUtils } from '@therealmanage/ciranet-ui-common';

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
  standalone: false
})
export class SignupComponent
  extends RecaptchaBaseComponent
  implements OnInit, AfterViewInit
{
  @ViewChild('validatorSignup', { static: false })
  validatorSignup: DxValidationGroupComponent;
  @ViewChild('passStrengthSuccess', { static: false })
  passStrengthSuccess: DxProgressBarComponent;
  @ViewChild('confirmPasswordValidator', { static: false })
  confirmPasswordValidator: DxValidatorComponent;
  @ViewChild('emailValidator', { static: false })
  emailValidator: DxValidatorComponent;
  @ViewChild('confirmEmailValidator', { static: false })
  confirmEmailValidator: DxValidatorComponent;

  @Input() userInfo: CreateUserParameterDto;
  @Output() onRegistrationUpdate = new EventEmitter<any>();

  isSmallScreen = false;
  passwordMode: string;
  passwordShowButton: object;
  passwordHideButton: object;
  helpButton: object;
  helpImage: string;
  passwordStrength = 0;

  loadingVisible = false;
  isHelpShow = false;
  passwordRules: any[] = [];

  registrationEditor: RegistrationEditorResultDto;
  passwordSpecialCharactersAllowable =
    Constants.passwordSpecialCharactersAllowable;
  emailPattern: RegExp = ValidationFormatConstants.emailPattern;
  backToLoginIcon = IconConstants.BACK_TO_LOGIN;
  iconCircleCheck = IconConstants.CIRCLE_CHECK;
  showGoogleAuth = true;
  showAccountAlreadyRegistered = false;

  private readonly GOOGLE_REGISTRATION_RESULT_KEY =
    'google_registration_result';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userService: UserService,
    private screenService: ScreenService,
    private passwordHelper: CCPasswordHelperService,
    protected override errorService: ServerErrorService,
    protected override envConfig: EnvironmentConfigService
  ) {
    super(errorService, envConfig);
  }

  ngOnInit(): void {
    this.initializePasswordRules();
    this.passwordMode = 'password';
    this.passwordShowButton = {
      icon: 'fas fa-eye',
      type: 'default',
      stylingMode: 'text',
      hint: 'Show Password',
      onClick: () => {
        this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
      }
    };
    this.passwordHideButton = {
      icon: 'fas fa-eye-slash',
      type: 'default',
      stylingMode: 'text',
      hint: 'Hide Password',
      onClick: () => {
        this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
      }
    };
    this.helpButton = {
      icon: 'fas fa-info-circle',
      type: 'default',
      stylingMode: 'text',
      hint: 'Show Help',
      onClick: () => {
        this.isHelpShow = true;
      }
    };
    this.helpImage = ImageConstants.helpAccountImage;
    this.updateScreenSize();
    RxjsUtils.safeSubscribe(this.screenService.changed, () => {
      this.updateScreenSize();
    });

    // Handle Google registration error
    this.handleGoogleRegistrationError();

    // Check if returning from Google OAuth registration
    this.checkGoogleRegistrationCallback();
  }

  private handleGoogleRegistrationError(): void {
    const error = this.route.snapshot.queryParams['error'];
    if (error !== 'google_failed') {
      return;
    }

    const message = this.route.snapshot.queryParams['message'];
    this.messageBox.showErrorMessage(
      'Registration Error',
      message || 'Google registration failed. Please try again.'
    );
  }

  private checkGoogleRegistrationCallback(): void {
    const fromGoogle = this.route.snapshot.queryParams['fromGoogle'];
    if (fromGoogle !== 'true') {
      return;
    }

    const registrationResultStr = sessionStorage.getItem(
      this.GOOGLE_REGISTRATION_RESULT_KEY
    );
    if (!registrationResultStr) {
      return;
    }

    let registrationResult: any;
    try {
      registrationResult = JSON.parse(registrationResultStr);
    } catch {
      sessionStorage.removeItem(this.GOOGLE_REGISTRATION_RESULT_KEY);
      this.messageBox.showErrorMessage(
        'Error',
        'Registration data is corrupted. Please start again.'
      );
      return;
    }

    // Populate userInfo with the Google registration data
    this.userInfo.customerID = registrationResult.customerID;
    this.userInfo.checkDigit = registrationResult.checkDigit;
    this.userInfo.email = registrationResult.email;
    this.userInfo.confirmEmail = registrationResult.email;
    this.userInfo.password = registrationResult.generatedPassword;
    this.userInfo.recaptchaToken = registrationResult.recaptchaToken;

    // Cleanup sessionStorage
    sessionStorage.removeItem(this.GOOGLE_REGISTRATION_RESULT_KEY);

    // Proceed with registration - call the API to get registration editor
    this.processGoogleRegistration(registrationResult);
  }

  private processGoogleRegistration(registrationResult: any): void {
    const baseParams: RegistrationEditorParametersDto =
      new RegistrationEditorParametersDto();
    baseParams.checkDigit = registrationResult.checkDigit;
    baseParams.customerId = registrationResult.customerID;
    baseParams.email = registrationResult.email;
    baseParams.clearCache = false;
    baseParams.recaptchaToken = registrationResult.recaptchaToken;

    this.loadingVisible = true;
    RxjsUtils.safeSubscribe(
      this.userService.getUserRegistrationEditor(baseParams),
      (result: RegistrationEditorResultDto) => {
        this.loadingVisible = false;
        this.registrationEditor = result;
        this.updateRegistration();
      },
      (error) => {
        this.loadingVisible = false;
        this.clearGoogleCredentials();
        this.processApiError(error);
      }
    );
  }

  ngAfterViewInit(): void {
    // fix the issue: if the user navigates back, the password strength is 0.
    if (!!this.userInfo.password) {
      this.updatePasswordStrength(this.userInfo.password);
    }
  }

  btnNextClick(): void {
    if (this.validatorSignup.instance.validate().isValid == false) {
      return;
    }

    if (this.userInfo.password.length < 8) {
      this.messageBox.showErrorMessage(
        'Error',
        'Password must have at least 8 characters.'
      );
      return;
    }

    if (
      Utils.isFalse(
        this.passwordHelper.hasSpecialCharacter(this.userInfo.password)
      )
    ) {
      this.messageBox.showErrorMessage(
        'Error',
        'Your password must contain at least one special character'
      );
      return;
    }

    if (Utils.isFalse(this.passwordHelper.hasNumeric(this.userInfo.password))) {
      this.messageBox.showErrorMessage(
        'Error',
        'Your password must contain at least one number.'
      );
      return;
    }

    if (this.passwordStrength < 5) {
      this.messageBox.showErrorMessage(
        'Error',
        `Your password must include at least one lowercase letter and one uppercase letter.`
      );
      return;
    }

    if (this.isValidRecaptcha()) {
      this.validateCredentialsAndProceed();
    }
  }

  private validateCredentialsAndProceed(): void {
    const credentials = new ValidateRegistrationCredentialsDto();
    credentials.email = this.userInfo.email;
    credentials.password = this.userInfo.password;

    this.loadingVisible = true;
    RxjsUtils.safeSubscribe(
      this.userService.validateRegistrationCredentials(credentials),
      (validationResult: ValidateRegistrationCredentialsResultDto) => {
        if (!validationResult.isValid) {
          this.loadingVisible = false;
          this.handleValidationFailure(validationResult);
          return;
        }
        this.proceedWithRegistration();
      },
      (error) => {
        this.loadingVisible = false;
        this.resetRecaptcha();
        this.userInfo.recaptchaToken = null;
        this.processApiError(error);
      }
    );
  }

  private handleValidationFailure(
    validationResult: ValidateRegistrationCredentialsResultDto
  ): void {
    // Check if email is already registered - show dedicated screen
    if (validationResult.hasEmailError()) {
      this.showAccountAlreadyRegistered = true;
      return;
    }

    const passwordError = validationResult.getPasswordError();
    if (passwordError) {
      this.messageBox.showErrorMessage('Error', passwordError);
    }
  }

  btnForgotPassword(): void {
    this.router.navigate(['user', 'forgotpassword']);
  }

  btnForgotUsername(): void {
    this.router.navigate(['user', 'forgotusername']);
  }

  private proceedWithRegistration(): void {
    const baseParams: RegistrationEditorParametersDto =
      new RegistrationEditorParametersDto();
    baseParams.checkDigit = this.userInfo.checkDigit;
    baseParams.customerId = this.userInfo.customerID;
    baseParams.email = this.userInfo.email;
    baseParams.clearCache = this.userInfo.clearCache;
    baseParams.recaptchaToken = this.recaptchaToken;
    this.userInfo.recaptchaToken = this.recaptchaToken;

    RxjsUtils.safeSubscribe(
      this.userService.getUserRegistrationEditor(baseParams),
      (result: RegistrationEditorResultDto) => {
        this.resetRecaptcha();
        this.loadingVisible = false;
        this.registrationEditor = result;
        this.updateRegistration();
      },
      (error) => {
        this.resetRecaptcha();
        this.loadingVisible = false;
        this.userInfo.recaptchaToken = null;
        this.processApiError(error);
      }
    );
  }

  btnBackToLogin(): void {
    this.router.navigate(['user', 'login']);
  }

  btnGoogleSignupClick(): void {
    // Validate Account# and Check Digit fields
    const customerID = this.userInfo.customerID;
    const checkDigit = this.userInfo.checkDigit;

    // Check if Account# is provided
    if (!customerID || customerID.trim() === '') {
      this.messageBox.showErrorMessage('Error', 'Account number is required');
      return;
    }

    // Check if Check Digit is provided
    if (!checkDigit || checkDigit.toString().trim() === '') {
      this.messageBox.showErrorMessage('Error', 'Check digit is required');
      return;
    }

    // Validate reCAPTCHA
    if (!this.isValidRecaptcha()) {
      return;
    }

    // Store registration data in sessionStorage for the callback
    const registerData = {
      customerID: customerID,
      checkDigit: checkDigit,
      recaptchaToken: this.recaptchaToken,
      origin: 'signup' // Track where the user came from
    };
    sessionStorage.setItem(
      'google_register_data',
      JSON.stringify(registerData)
    );

    const spaOrigin = window.location.origin;
    const googleRegisterUrl = `${this.envConfig.ccAuthenticationApiUrl}google-register-challenge?portalType=RP&spaOrigin=${encodeURIComponent(spaOrigin)}`;
    window.location.href = googleRegisterUrl;
  }

  runPasswordValidation(e: any): void {
    const value = e.event.target.value;
    this.updatePasswordStrength(value);
    this.passwordRules.forEach((rule) => {
      rule.isValid = rule.check(value);
    });
  }

  updatePasswordStrength(passValue: string): void {
    this.passwordStrength = this.passwordHelper.getPasswordStrength(passValue);
    this.passStrengthSuccess.value = this.passwordStrength;
  }

  emailComparison = () => {
    return this.userInfo.email;
  };

  passwordComparison = () => {
    return this.userInfo.password;
  };

  getPasswordStrengthText(value: number): string {
    return 'Password Strength ' + Math.round(value * 20) + '%';
  }

  updateScreenSize(): void {
    this.isSmallScreen = this.screenService.isSmallScreen();
  }

  private updateRegistration(): void {
    this.onRegistrationUpdate.emit({
      userInfo: this.userInfo,
      registrationEditor: this.registrationEditor
    });
  }

  private clearGoogleCredentials(): void {
    this.userInfo.email = '';
    this.userInfo.confirmEmail = '';
    this.userInfo.password = '';
  }

  private initializePasswordRules(): void {
    this.passwordRules = [
      {
        label: '8 characters',
        isValid: false,
        check: (value: string) => value.length >= 8
      },
      {
        label: 'One lowercase (a-z) character.',
        isValid: false,
        check: (value: string) => this.passwordHelper.hasLowerCase(value)
      },
      {
        label: 'One uppercase (A-Z) character.',
        isValid: false,
        check: (value: string) => this.passwordHelper.hasUpperCase(value)
      },
      {
        label: 'One number (0-9) character.',
        isValid: false,
        check: (value: string) => this.passwordHelper.hasNumeric(value)
      },
      {
        label: `Special characters (${this.passwordSpecialCharactersAllowable})`,
        isValid: false,
        check: (value: string) => this.passwordHelper.hasSpecialCharacter(value)
      }
    ];
  }
}
